import { useEffect, useId, useState } from "react";
import type { FormEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { demoApi, problemMessage } from "@/api/client";
import { formatMoney } from "@/lib/format";
import type { BusinessEntity, PartnerSplitDollarReport } from "@/api/types";
import "./ReportsPartnerSplitsDollar.css";

type ReportStatus = "idle" | "loading" | "ready" | "error";
type Basis = "cash" | "accrual";

const BASES: { key: Basis; label: string }[] = [
  { key: "cash", label: "Cash" },
  { key: "accrual", label: "Accrual" },
];

/**
 * Partner split allocation, dollar — Reports' dollar half of the legacy
 * `rptctl_parttran` pair (`ReportsPartnerSplits.tsx` already ships the percentage half,
 * `rpt_partperc`). ef-054-1, ADR 0009 §6 ("Reports is one picker" — a report renders
 * inside the template at `/reports/{slug}`, no area tab strip of its own; "All reports"
 * below is the only way back).
 *
 * <p>
 * ENTITY IS REQUIRED, SO THIS SCREEN CARRIES ITS OWN PICKER — unlike a whole-farm-default
 * report, `PartnerSplitsDollarReportEndpoints` has no "resolve the tenant's own
 * single/oldest entity" fallback (D2, same posture `income-statement`'s and
 * `partner-splits`' own contracts take). Same shape `ReportsPartnerSplits.tsx`'s own
 * "Entity" picker already uses: seeded once from the server's own entity list, a "Show"
 * button fetches on click.
 * </p>
 *
 * <p>
 * CASH/ACCRUAL TOGGLE — same shape `ReportsIncomeStatement.tsx`'s own toggle: it refetches
 * immediately once a report has already been shown for the selected entity (a farmer
 * comparing bases should not have to press Show again), but never fetches on its own
 * before Show has been pressed once.
 * </p>
 *
 * <p>
 * NOTHING IS FETCHED UNTIL Show IS PRESSED — same reasoning `ReportsPartnerSplits.tsx`'s
 * own remarks give: a dollar allocation read by an owner who has chosen an entity should
 * not show a number for an entity nobody asked about. Only the entity list (for the
 * picker itself) loads eagerly.
 * </p>
 */
export function ReportsPartnerSplitsDollar() {
  const entityFieldId = useId();

  const [entities, setEntities] = useState<BusinessEntity[] | null>(null);
  const [entityId, setEntityId] = useState<string>("");
  const [basis, setBasis] = useState<Basis>("cash");

  const [report, setReport] = useState<PartnerSplitDollarReport | null>(null);
  const [status, setStatus] = useState<ReportStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    demoApi
      .getEntities(controller.signal)
      .then((rows) => {
        setEntities(rows);
        // Seeded once, from the server's own list — never left blank when there is
        // exactly one entity to pick, the common single-entity farm this booth demo
        // itself models (db/seed/0002_seed_demo.sql).
        setEntityId((current) => current || (rows[0]?.id ?? ""));
      })
      .catch(() => setEntities([]));
    return () => controller.abort();
  }, []);

  const canSubmit = entityId !== "" && status !== "loading";

  async function loadReport(id: string, basisToLoad: Basis): Promise<void> {
    setStatus("loading");
    setError(null);
    try {
      const result = await demoApi.getPartnerSplitsDollarReport({ entityId: id, basis: basisToLoad });
      setReport(result);
      setStatus("ready");
    } catch (err) {
      setReport(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load the partner split allocation report."));
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;
    await loadReport(entityId, basis);
  }

  function handleBasisChange(next: Basis): void {
    setBasis(next);
    // Only refetches when a report is already on screen for the chosen entity — same
    // "nothing until Show" posture the rest of this screen keeps otherwise.
    if (entityId !== "" && (status === "ready" || status === "error")) void loadReport(entityId, next);
  }

  /**
   * The export is its own request, fired on the click — not fetched alongside the
   * report, same reasoning `ReportsPartnerSplits.tsx`'s own identical download gives.
   */
  const [csvState, setCsvState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "done"; rows: number } | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function downloadCsv(): Promise<void> {
    if (entityId === "") return;
    setCsvState({ kind: "busy" });
    try {
      const csv = await demoApi.getPartnerSplitsDollarCsv({ entityId, basis });

      // The bytes are the server writer's output verbatim — same "add a blob and a
      // filename and nothing else" discipline `ReportsPartnerSplits.tsx`'s own download
      // follows.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `easyfarm-partner-splits-dollar-${basis}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const rows = Math.max(csv.trim().split("\n").length - 1, 0);
      setCsvState({ kind: "done", rows });
    } catch (err) {
      setCsvState({
        kind: "error",
        message: problemMessage(err, "Could not build the partner split file."),
      });
    }
  }

  function handleAllReportsClick(): void {
    // Self-contained, same device every other report screen's own handler already uses
    // rather than threading a `navigate` prop through every screen this job touches:
    // pushState doesn't fire popstate on its own, so App.tsx's existing listener is told
    // directly.
    window.history.pushState({}, "", "/reports");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  const rows = report?.rows ?? [];

  return (
    <main className="reports-partner-splits-dollar">
      <PageHeader
        title="Partner split allocation, dollar"
        subtitle="Each partner's dollar share of an entity's net income"
        action={
          <div className="reports-partner-splits-dollar__toggle" role="group" aria-label="Accounting basis">
            {BASES.map((b) => (
              <button
                key={b.key}
                type="button"
                aria-pressed={basis === b.key}
                className={`reports-partner-splits-dollar__toggle-btn${
                  basis === b.key ? " reports-partner-splits-dollar__toggle-btn--on" : ""
                }`}
                onClick={() => handleBasisChange(b.key)}
              >
                {b.label}
              </button>
            ))}
          </div>
        }
      />

      <a
        className="reports-partner-splits-dollar__all-reports"
        href="/reports"
        onClick={(event) => {
          event.preventDefault();
          handleAllReportsClick();
        }}
      >
        ← All reports
      </a>

      <section
        className="reports-partner-splits-dollar__panel"
        aria-labelledby="reports-partner-splits-dollar-heading"
        data-state={status}
      >
        <h2 id="reports-partner-splits-dollar-heading" className="reports-partner-splits-dollar__heading">
          Allocation
        </h2>

        <form className="reports-partner-splits-dollar__form" onSubmit={(event) => void handleSubmit(event)}>
          <div className="reports-partner-splits-dollar__field">
            <label htmlFor={entityFieldId}>Entity</label>
            <select
              id={entityFieldId}
              value={entityId}
              onChange={(event) => setEntityId(event.target.value)}
              disabled={entities === null || entities.length === 0}
            >
              {entities === null ? (
                <option value="">Loading…</option>
              ) : entities.length === 0 ? (
                <option value="">No entities on this farm yet</option>
              ) : (
                entities.map((entity) => (
                  <option key={entity.id} value={entity.id}>
                    {entity.name}
                  </option>
                ))
              )}
            </select>
          </div>

          <button
            type="submit"
            className="btn btn--primary reports-partner-splits-dollar__show"
            disabled={!canSubmit}
          >
            {status === "loading" ? "Loading…" : "Show"}
          </button>
        </form>

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the partner split allocation report."
            detail={error ?? undefined}
          />
        )}

        {status === "ready" && report !== null && (
          <>
            <p className="reports-partner-splits-dollar__net">
              Net income ({report.basis.toLowerCase()} basis): <strong>{formatMoney(report.netIncome)}</strong>
            </p>

            <div
              className="reports-partner-splits-dollar__table-wrap"
              tabIndex={0}
              role="group"
              aria-labelledby="reports-partner-splits-dollar-heading"
            >
              <table className="reports-partner-splits-dollar__table ruled-rows">
                <thead>
                  <tr>
                    <th scope="col">Partner</th>
                    <th scope="col" className="reports-partner-splits-dollar__num">
                      Percentage
                    </th>
                    <th scope="col" className="reports-partner-splits-dollar__num">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="reports-partner-splits-dollar__empty-row">
                        No active partner splits for this entity.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id}>
                        <th scope="row" className="reports-partner-splits-dollar__partner">
                          {row.partnerName}
                        </th>
                        <td className="reports-partner-splits-dollar__num">{row.percentage.toFixed(2)}%</td>
                        <td className="reports-partner-splits-dollar__num reports-partner-splits-dollar__amount">
                          {formatMoney(row.amount)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="reports-partner-splits-dollar__export">
              <h3 className="reports-partner-splits-dollar__export-title">
                Partner split allocation for your records
              </h3>
              <p className="reports-partner-splits-dollar__export-copy">
                The same rows above, as a CSV a spreadsheet can open.
              </p>
              <button
                type="button"
                className="btn btn--secondary reports-partner-splits-dollar__export-btn"
                disabled={csvState.kind === "busy"}
                onClick={() => void downloadCsv()}
              >
                {csvState.kind === "busy" ? "Building the file…" : "Download CSV"}
              </button>

              {csvState.kind === "done" && (
                <p className="reports-partner-splits-dollar__export-result" role="status">
                  {csvState.rows} {csvState.rows === 1 ? "line" : "lines"} saved.
                </p>
              )}

              {csvState.kind === "error" && (
                <p className="reports-partner-splits-dollar__export-error" role="alert">
                  {csvState.message} Nothing was downloaded.
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
